/**
 * IPD Billing OT Approvals Queue.
 *
 * Provides billing users with an operational worklist to audit surgical requests
 * against live patient admission ledgers (captured debit charges vs deposits vs net balance).
 *
 * Workflows:
 * 1. Elective Surgery Clearance: Approving moves request to APPROVED, unlocking theatre scheduling.
 * 2. Financial Hold: Placing on hold marks request DENIED with mandatory audit reason.
 * 3. Emergency Exemption: Emergency surgeries bypass the scheduling hold per clinical governance.
 * 4. Consignment Implants: Physical receipt directly into OT Store creates GRN and debits ledger.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import {
  fetchOtRequests,
  decideOtRequestBilling,
  receiveConsignmentImplant,
} from "./ot/api/otApi";

const STATUS_TABS = [
  { id: "PENDING", label: "Pending Clearance" },
  { id: "APPROVED", label: "Cleared / Approved" },
  { id: "DENIED", label: "Held / Denied" },
  { id: "ALL", label: "All Cases" },
];

/**
 * Derives badge visual properties and label based on billing status and urgency.
 *
 * @param {string} status - PENDING, APPROVED, or DENIED
 * @param {string} schedulingType - ELECTIVE, EMERGENCY, or DAYCARE
 * @returns {{ bg: string, color: string, border: string, label: string }}
 */
function statusBadge(status, schedulingType) {
  const isEmergency = (schedulingType || "").toUpperCase() === "EMERGENCY";
  const st = (status || "PENDING").toUpperCase();

  if (st === "APPROVED") {
    return {
      bg: "#ecfdf5",
      color: "#047857",
      border: "#a7f3d0",
      label: isEmergency ? "Emergency Cleared" : "Billing Approved",
    };
  }
  if (st === "DENIED") {
    return {
      bg: "#fef2f2",
      color: "#b91c1c",
      border: "#fecaca",
      label: "Held / Denied",
    };
  }
  return {
    bg: "#fffbeb",
    color: "#b45309",
    border: "#fde68a",
    label: isEmergency ? "Emergency (Hold Bypassed)" : "Pending Clearance",
  };
}

/**
 * Modal dialog for granting billing clearance or placing an OT request on financial hold.
 */
function DecisionModal({ request, mode, busy, onClose, onSubmit }) {
  const isApprove = mode === "approve";
  const [notes, setNotes] = useState("");

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(13,35,63,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="decision-modal-title"
        tabIndex={-1}
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "28px",
          width: "480px",
          maxWidth: "92vw",
          boxShadow: "0 24px 64px rgba(13,35,63,0.28)",
          outline: "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: isApprove ? "#ecfdf5" : "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SvgIcon
              name={isApprove ? "check" : "alert-triangle"}
              size={18}
              color={isApprove ? C.success : C.danger}
            />
          </div>
          <div>
            <h3 id="decision-modal-title" style={{ margin: 0, fontSize: "17px", fontWeight: "800", color: C.text }}>
              {isApprove ? "Approve Surgery for Theatre" : "Place Surgery on Financial Hold"}
            </h3>
            <p style={{ margin: "2px 0 0", fontSize: "12px", color: C.textSub }}>
              Request #{request.id} · {request.patient_name} ({request.health_record_id || request.patient_id})
            </p>
          </div>
        </div>

        <div
          style={{
            background: "#f8fafc",
            borderRadius: "8px",
            padding: "12px 16px",
            margin: "16px 0",
            border: `1px solid ${C.border}`,
            fontSize: "12px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
            <span style={{ color: C.textSub }}>Procedure:</span>
            <span style={{ fontWeight: "700", color: C.text }}>{request.procedure_name || "—"}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
            <span style={{ color: C.textSub }}>Surgeon:</span>
            <span style={{ fontWeight: "600", color: C.text }}>{request.surgeon_name || "—"}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
            <span style={{ color: C.textSub }}>Total Debits (Captured):</span>
            <span style={{ fontWeight: "600", color: C.text }}>{fmtINR(request.billing_ledger_charges)}</span>
          </div>
          {request.tentative_cost != null && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
              <span style={{ color: C.textSub }}>Implant estimate (not yet charged):</span>
              <span style={{ fontWeight: "600", color: C.text }}>{fmtINR(request.tentative_cost)}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
            <span style={{ color: C.textSub }}>Paid / Deposits:</span>
            <span style={{ fontWeight: "600", color: C.success }}>{fmtINR(request.billing_ledger_paid)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: "6px" }}>
            <span style={{ fontWeight: "700", color: C.text }}>Net Balance:</span>
            <span
              style={{
                fontWeight: "800",
                color: (request.billing_ledger_balance || 0) > 0 ? C.danger : C.success,
              }}
            >
              {fmtINR(request.billing_ledger_balance)}
            </span>
          </div>
        </div>

        <label
          htmlFor="decision-notes-input"
          style={{
            fontSize: "11px",
            fontWeight: "700",
            color: C.textSub,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            display: "block",
            marginBottom: "5px",
          }}
        >
          {isApprove ? "Approval Clearance Notes (Optional)" : "Reason for Financial Hold *"}
        </label>
        <textarea
          id="decision-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder={
            isApprove
              ? "e.g. Deposit verified, insurance pre-auth sanctioned"
              : "State required deposit amount or reason for hold"
          }
          style={{
            width: "100%",
            padding: "9px 12px",
            border: `1px solid ${C.border}`,
            borderRadius: "7px",
            fontSize: "13px",
            color: C.text,
            outline: "none",
            boxSizing: "border-box",
            resize: "vertical",
          }}
        />

        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              flex: 1,
              padding: "11px",
              background: "#fff",
              color: C.textSub,
              border: `1px solid ${C.border}`,
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(isApprove ? "APPROVE" : "DENY", notes.trim())}
            disabled={busy || (!isApprove && !notes.trim())}
            style={{
              flex: 2,
              padding: "11px",
              background: isApprove ? C.success : C.danger,
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "700",
              cursor: busy ? "wait" : "pointer",
              opacity: !isApprove && !notes.trim() ? 0.6 : 1,
            }}
          >
            {busy ? "Processing…" : isApprove ? "Grant Billing Clearance" : "Confirm Financial Hold"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal dialog for receiving consignment implants directly into the OT Store
 * and posting the corresponding charge event to the patient's billing ledger.
 */
function ConsignmentModal({ request, busy, onClose, onSubmit }) {
  const details = request.implant_details || {};
  const [itemName, setItemName] = useState(details.name || request.procedure_name || "Consignment Implant");
  const [quantity, setQuantity] = useState(String(details.quantity || "1"));
  const [unitPrice, setUnitPrice] = useState(String(details.unit_price || "0.00"));
  const [batchNo, setBatchNo] = useState(details.batch_no || "CONSIGN");
  const [serialNo, setSerialNo] = useState(details.serial_no || `SN-${request.id}`);

  const gross = (parseFloat(quantity || 0) * parseFloat(unitPrice || 0)).toFixed(2);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(13,35,63,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="consign-modal-title"
        tabIndex={-1}
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "28px",
          width: "500px",
          maxWidth: "92vw",
          boxShadow: "0 24px 64px rgba(13,35,63,0.28)",
          outline: "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "#eff6ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SvgIcon name="upload" size={18} color={C.primary} />
          </div>
          <div>
            <h3 id="consign-modal-title" style={{ margin: 0, fontSize: "17px", fontWeight: "800", color: C.text }}>
              Receive & Charge Consignment Implant
            </h3>
            <p style={{ margin: "2px 0 0", fontSize: "12px", color: C.textSub }}>
              Direct OT Store Receipt · Auto-Posts Charge to IPD Ledger
            </p>
          </div>
        </div>

        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "8px",
            padding: "10px 14px",
            margin: "14px 0",
            fontSize: "12px",
            color: "#166534",
          }}
        >
          <strong>PO Number:</strong> {request.consignment_po_number || `PO-CONSIGN-${request.id}`}
          <br />
          Receiving generates GRN and directly posts the debit charge to Patient:{" "}
          <strong>{request.patient_name}</strong> ({request.health_record_id || request.patient_id}).
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div>
            <label
              htmlFor="consign-item-name"
              style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, display: "block", marginBottom: "4px" }}
            >
              Item Name / Description
            </label>
            <input
              id="consign-item-name"
              type="text"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="consign-quantity"
                style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, display: "block", marginBottom: "4px" }}
              >
                Quantity
              </label>
              <input
                id="consign-quantity"
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="consign-unit-price"
                style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, display: "block", marginBottom: "4px" }}
              >
                Unit Price (₹)
              </label>
              <input
                id="consign-unit-price"
                type="number"
                step="0.01"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="consign-gross-charge"
                style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, display: "block", marginBottom: "4px" }}
              >
                Gross Charge (₹)
              </label>
              <input
                id="consign-gross-charge"
                type="text"
                disabled
                value={`₹${gross}`}
                style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px", background: "#f8fafc", fontWeight: "700", boxSizing: "border-box" }}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="consign-batch-no"
                style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, display: "block", marginBottom: "4px" }}
              >
                Batch No
              </label>
              <input
                id="consign-batch-no"
                type="text"
                value={batchNo}
                onChange={(e) => setBatchNo(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="consign-serial-no"
                style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, display: "block", marginBottom: "4px" }}
              >
                Serial / Lot No
              </label>
              <input
                id="consign-serial-no"
                type="text"
                value={serialNo}
                onChange={(e) => setSerialNo(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              flex: 1,
              padding: "11px",
              background: "#fff",
              color: C.textSub,
              border: `1px solid ${C.border}`,
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onSubmit({
                item_name: itemName,
                quantity,
                unit_price: unitPrice,
                batch_no: batchNo,
                serial_no: serialNo,
              })
            }
            disabled={busy || !itemName || !quantity}
            style={{
              flex: 2,
              padding: "11px",
              background: C.primary,
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "700",
              cursor: busy ? "wait" : "pointer",
            }}
          >
            {busy ? "Posting to Ledger…" : "Generate GRN & Post Charge"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Main IPD Billing OT Approvals workbench view.
 *
 * Renders tabbed request lists, financial ledger summaries, search filter,
 * and handles clearance decisions and consignment implant receipts.
 */
export default function IPDBillingOtApprovals() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("PENDING");
  const [searchQuery, setSearchQuery] = useState("");
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");

  const [decisionModal, setDecisionModal] = useState(null); // { mode: "approve" | "deny", request }
  const [consignModal, setConsignModal] = useState(null); // request

  const loadRequests = useCallback(async () => {
    setIsLoading(true);
    setApiError("");
    try {
      // Fetch billing queue enriched with financials
      const res = await fetchOtRequests({ billing_queue: 1 });
      const rows = res?.requests || (Array.isArray(res) ? res : []);
      setRequests(rows);
    } catch (err) {
      setApiError(err.message || "Failed to load OT approval requests");
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const counts = useMemo(() => {
    const c = { PENDING: 0, APPROVED: 0, DENIED: 0, ALL: requests.length };
    requests.forEach((r) => {
      const st = (r.billing_status || "PENDING").toUpperCase();
      if (st in c) c[st] += 1;
    });
    return c;
  }, [requests]);

  const visible = useMemo(() => {
    return requests.filter((r) => {
      const st = (r.billing_status || "PENDING").toUpperCase();
      if (filter !== "ALL" && st !== filter) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const name = (r.patient_name || "").toLowerCase();
      const uhid = (r.health_record_id || r.patient_id || "").toLowerCase();
      const proc = (r.procedure_name || "").toLowerCase();
      const surg = (r.surgeon_name || "").toLowerCase();
      const idStr = String(r.id);
      return (
        name.includes(q) ||
        uhid.includes(q) ||
        proc.includes(q) ||
        surg.includes(q) ||
        idStr.includes(q)
      );
    });
  }, [requests, filter, searchQuery]);

  const handleDecisionSubmit = async (decision, notes) => {
    if (!decisionModal?.request) return;
    setBusy(true);
    setApiError("");
    setApiSuccess("");
    try {
      const res = await decideOtRequestBilling(decisionModal.request.id, {
        decision,
        notes,
      });
      if (res?.ok || res?.status === "Success") {
        setApiSuccess(
          decision === "APPROVE"
            ? `Request #${decisionModal.request.id} cleared for surgery.`
            : `Request #${decisionModal.request.id} placed on financial hold.`
        );
        setDecisionModal(null);
        await loadRequests();
      } else {
        setApiError(res?.message || "Failed to record billing decision");
      }
    } catch (err) {
      setApiError(err.message || "Failed to record billing decision");
    } finally {
      setBusy(false);
    }
  };

  const handleConsignmentSubmit = async (payload) => {
    if (!consignModal) return;
    setBusy(true);
    setApiError("");
    setApiSuccess("");
    try {
      const res = await receiveConsignmentImplant(consignModal.id, payload);
      if (res?.ok || res?.status === "Success") {
        setApiSuccess(
          `Consignment received! ${res?.grn_number || "GRN generated"} and posted to patient's ledger.`
        );
        setConsignModal(null);
        await loadRequests();
      } else {
        setApiError(res?.message || "Failed to receive consignment implant");
      }
    } catch (err) {
      setApiError(err.message || "Failed to receive consignment implant");
    } finally {
      setBusy(false);
    }
  };

  return (
    <IPDBillingLayout activeNav="ot-approvals">
      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>
              OT Surgical Clearance & Implant Approvals
            </h2>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
              Evaluate financial ledger snapshots before theatre scheduling. Review consignment POs and post direct implant debits.
            </p>
            {apiError && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.danger, fontWeight: "600" }}>{apiError}</p>}
            {apiSuccess && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.success, fontWeight: "600" }}>{apiSuccess}</p>}
          </div>
          <button
            onClick={loadRequests}
            disabled={busy || isLoading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              padding: "9px 16px",
              background: "#fff",
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            <SvgIcon name="sync" size={15} color={C.text} /> Refresh
          </button>
        </div>

        {/* Search & Tabs */}
        <div
          style={{
            background: C.card,
            borderRadius: "12px",
            border: `1px solid ${C.border}`,
            boxShadow: "0 2px 8px rgba(13,35,63,0.07)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "14px 18px",
              borderBottom: `1px solid ${C.border}`,
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {STATUS_TABS.map((t) => {
                const active = filter === t.id;
                const n = counts[t.id] ?? 0;
                return (
                  <button
                    key={t.id}
                    onClick={() => setFilter(t.id)}
                    style={{
                      padding: "7px 14px",
                      borderRadius: "8px",
                      border: `1px solid ${active ? C.primary : C.border}`,
                      background: active ? C.primary : "#fff",
                      color: active ? "#fff" : C.text,
                      fontSize: "12px",
                      fontWeight: "700",
                      cursor: "pointer",
                    }}
                  >
                    {t.label} · {n}
                  </button>
                );
              })}
            </div>

            <div style={{ minWidth: "260px" }}>
              <input
                type="text"
                aria-label="Search surgical clearance requests"
                placeholder="Search patient, UHID, procedure, surgeon…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  fontSize: "12px",
                  border: `1px solid ${C.border}`,
                  borderRadius: "8px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = C.primary;
                  e.target.style.boxShadow = "0 0 0 2px rgba(37,99,235,0.15)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = C.border;
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {isLoading ? (
            <div style={{ padding: "40px", textAlign: "center", color: C.textSub, fontSize: "13px" }}>
              Loading OT clearance requests…
            </div>
          ) : visible.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: C.textSub, fontSize: "13px" }}>
              No {filter === "ALL" ? "" : `${filter.toLowerCase()} `}requests found.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr
                    style={{
                      textAlign: "left",
                      color: C.textSub,
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      background: "#fafafa",
                    }}
                  >
                    <th style={{ padding: "10px 16px" }}>Req ID / Date</th>
                    <th style={{ padding: "10px 16px" }}>Patient</th>
                    <th style={{ padding: "10px 14px" }}>Procedure & Team</th>
                    <th style={{ padding: "10px 14px" }}>Type</th>
                    <th style={{ padding: "10px 14px", textAlign: "right" }}>Financial Ledger Snapshot</th>
                    <th style={{ padding: "10px 14px" }}>Billing Status</th>
                    <th style={{ padding: "10px 14px" }}>Consignment Implant</th>
                    <th style={{ padding: "10px 16px", textAlign: "right" }}>Clearance Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => {
                    const badge = statusBadge(r.billing_status, r.scheduling_type);
                    const isEmergency = (r.scheduling_type || "").toUpperCase() === "EMERGENCY";
                    const isPending = (r.billing_status || "PENDING").toUpperCase() === "PENDING";
                    const isDenied = (r.billing_status || "").toUpperCase() === "DENIED";
                    const hasConsignment = Boolean(r.implant_required);
                    const isConsignmentCharged = r.implant_status === "CHARGED";

                    return (
                      <tr key={r.id} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ fontWeight: "700", color: C.text }}>#{r.id}</div>
                          <div style={{ fontSize: "11px", color: C.textSub }}>
                            {r.requested_date} {r.requested_start_time || ""}
                          </div>
                        </td>

                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ fontWeight: "700", color: C.text }}>{r.patient_name || "—"}</div>
                          <div style={{ fontSize: "11px", color: C.textSub }}>
                            {r.health_record_id || r.patient_id} · {r.gender || ""} {r.age ? `${r.age}y` : ""}
                          </div>
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ fontWeight: "600", color: C.text }}>{r.procedure_name || "—"}</div>
                          <div style={{ fontSize: "11px", color: C.textSub }}>
                            Surgeon: <strong>{r.surgeon_name || "—"}</strong>
                          </div>
                          {r.anaesthetist_name && (
                            <div style={{ fontSize: "11px", color: C.textSub }}>
                              Anaesth: <strong>{r.anaesthetist_name}</strong>
                            </div>
                          )}
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          {isEmergency ? (
                            <span
                              style={{
                                display: "inline-block",
                                padding: "3px 8px",
                                borderRadius: "6px",
                                fontSize: "11px",
                                fontWeight: "800",
                                background: "#fee2e2",
                                color: "#dc2626",
                                border: "1px solid #fca5a5",
                              }}
                            >
                              EMERGENCY
                            </span>
                          ) : (
                            <span
                              style={{
                                display: "inline-block",
                                padding: "3px 8px",
                                borderRadius: "6px",
                                fontSize: "11px",
                                fontWeight: "600",
                                background: "#f1f5f9",
                                color: "#475569",
                              }}
                            >
                              {r.scheduling_type || "ELECTIVE"}
                            </span>
                          )}
                        </td>

                        <td style={{ padding: "12px 14px", textAlign: "right" }}>
                          <div style={{ fontSize: "12px" }}>
                            <span style={{ color: C.textSub }}>Debits: </span>
                            <strong style={{ color: C.text }}>{fmtINR(r.billing_ledger_charges)}</strong>
                          </div>
                          {r.tentative_cost != null && (
                            <div style={{ fontSize: "12px" }}>
                              <span style={{ color: C.textSub }}>Implant est.: </span>
                              <strong style={{ color: C.text }}>{fmtINR(r.tentative_cost)}</strong>
                            </div>
                          )}
                          <div style={{ fontSize: "12px" }}>
                            <span style={{ color: C.textSub }}>Paid: </span>
                            <strong style={{ color: C.success }}>{fmtINR(r.billing_ledger_paid)}</strong>
                          </div>
                          <div style={{ fontSize: "12px", borderTop: "1px dashed #e2e8f0", marginTop: "2px", paddingTop: "2px" }}>
                            <span style={{ color: C.textSub }}>Bal: </span>
                            <strong style={{ color: (r.billing_ledger_balance || 0) > 0 ? C.danger : C.success }}>
                              {fmtINR(r.billing_ledger_balance)}
                            </strong>
                          </div>
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 9px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              fontWeight: "700",
                              background: badge.bg,
                              color: badge.color,
                              border: `1px solid ${badge.border}`,
                            }}
                          >
                            {badge.label}
                          </span>
                          {r.billing_approved_by_name && (
                            <div style={{ fontSize: "10px", color: C.textSub, marginTop: "4px" }}>
                              by {r.billing_approved_by_name}
                            </div>
                          )}
                          {r.billing_notes && (
                            <div style={{ fontSize: "11px", color: C.textSub, fontStyle: "italic", marginTop: "2px", maxWidth: "160px" }}>
                              "{r.billing_notes}"
                            </div>
                          )}
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          {hasConsignment ? (
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "2px 7px",
                                    borderRadius: "5px",
                                    fontSize: "10px",
                                    fontWeight: "700",
                                    background: "#e0e7ff",
                                    color: "#3730a3",
                                  }}
                                >
                                  {r.consignment_po_number || `PO-CONSIGN-${r.id}`}
                                </span>
                                {isConsignmentCharged ? (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "2px 7px",
                                      borderRadius: "5px",
                                      fontSize: "10px",
                                      fontWeight: "700",
                                      background: "#dcfce7",
                                      color: "#15803d",
                                    }}
                                  >
                                    {r.consignment_grn_number || "CHARGED"}
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => setConsignModal(r)}
                                    disabled={busy}
                                    style={{
                                      padding: "5px 10px",
                                      minHeight: "28px",
                                      background: C.primary,
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "6px",
                                      fontSize: "11px",
                                      fontWeight: "700",
                                      cursor: "pointer",
                                    }}
                                  >
                                    Receive & Charge
                                  </button>
                                )}
                              </div>
                              {r.implant_details?.name && (
                                <div style={{ fontSize: "11px", color: C.textSub, marginTop: "3px" }}>
                                  {r.implant_details.name} (x{r.implant_details.quantity || 1})
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: "11px", color: C.textSub }}>No implant</span>
                          )}
                        </td>

                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                            {(isPending || isDenied) && (
                              <button
                                onClick={() => setDecisionModal({ mode: "approve", request: r })}
                                disabled={busy}
                                style={{
                                  padding: "7px 14px",
                                  minHeight: "34px",
                                  background: C.success,
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: "700",
                                  cursor: "pointer",
                                }}
                              >
                                {isDenied ? "Re-clear" : "Approve"}
                              </button>
                            )}
                            {isPending && !isEmergency && (
                              <button
                                onClick={() => setDecisionModal({ mode: "deny", request: r })}
                                disabled={busy}
                                style={{
                                  padding: "7px 14px",
                                  minHeight: "34px",
                                  background: "#fff",
                                  color: C.danger,
                                  border: `1px solid ${C.danger}`,
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: "700",
                                  cursor: "pointer",
                                }}
                              >
                                Hold
                              </button>
                            )}
                            {r.billing_status === "APPROVED" && (
                              <span style={{ fontSize: "12px", color: C.success, fontWeight: "600" }}>
                                ✓ Cleared
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {decisionModal && (
        <DecisionModal
          request={decisionModal.request}
          mode={decisionModal.mode}
          busy={busy}
          onClose={() => setDecisionModal(null)}
          onSubmit={handleDecisionSubmit}
        />
      )}

      {consignModal && (
        <ConsignmentModal
          request={consignModal}
          busy={busy}
          onClose={() => setConsignModal(null)}
          onSubmit={handleConsignmentSubmit}
        />
      )}
    </IPDBillingLayout>
  );
}
